import React from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  getProductSKUs,
  getProductSKUCategories,
  createProductSKU,
  bulkCreateProductSKUs,
  deleteProductSKU,
  touchProductSKU,
  updateProductSKU,
  groupAnalyzeProductSKU,
  filterProductSKUs,
} from "@/services/api/product-sku.service";
import {
  ProductSKUResponse,
  ProductSKUFilterResponse,
  CreateProductSKUPayload,
  BulkCreateProductSKUPayload,
  UpdateProductSKUPayload,
  GroupAnalyzePayload,
} from "@/types/product-sku.types";

export const productSKUQueryKey = (brandId: string) => [
  "product-skus",
  brandId,
];

export const productSKUCategoriesQueryKey = (brandId: string) => [
  "product-sku-categories",
  brandId,
];

export function useProductSKUs(brandId: string | null) {
  return useQuery({
    queryKey: productSKUQueryKey(brandId ?? ""),
    queryFn: () => getProductSKUs(brandId!),
    enabled: !!brandId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useProductSKUCategoriesList(brandId: string | null) {
  return useQuery({
    queryKey: productSKUCategoriesQueryKey(brandId ?? ""),
    queryFn: () => getProductSKUCategories(brandId!),
    enabled: !!brandId,
    staleTime: 1000 * 60 * 2,
  });
}

/** Returns sorted unique category strings derived from existing SKUs */
export function useProductSKUCategories(brandId: string | null) {
  const { data: skus = [] } = useProductSKUs(brandId);
  return React.useMemo(
    () =>
      Array.from(
        new Set(skus.map((s) => s.category).filter(Boolean) as string[])
      ).sort((a, b) => a.localeCompare(b)),
    [skus]
  );
}

export function useTouchProductSKU(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skuId: string) => touchProductSKU(brandId!, skuId),
    onSuccess: (updatedSku) => {
      queryClient.setQueryData<ProductSKUResponse[]>(
        productSKUQueryKey(brandId ?? ""),
        (old) =>
          old?.map((s) => (s.id === updatedSku.id ? updatedSku : s)) ?? []
      );
    },
  });
}

export function useDeleteProductSKU(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skuId: string) => deleteProductSKU(brandId!, skuId),
    onSuccess: (_data, skuId) => {
      queryClient.setQueryData<ProductSKUResponse[]>(
        productSKUQueryKey(brandId ?? ""),
        (old) => old?.filter((s) => s.id !== skuId) ?? []
      );
      queryClient.invalidateQueries({
        queryKey: ["product-skus-filter", brandId],
      });
      queryClient.invalidateQueries({
        queryKey: productSKUCategoriesQueryKey(brandId ?? ""),
      });
    },
  });
}

export function useCreateProductSKU(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProductSKUPayload) =>
      createProductSKU(brandId!, payload),
    onMutate: async (payload) => {
      const key = productSKUQueryKey(brandId ?? "");
      await queryClient.cancelQueries({ queryKey: key });
      const previousSkus = queryClient.getQueryData<ProductSKUResponse[]>(key);

      const tempId = `_optimistic_${Date.now()}`;
      const optimisticSku: ProductSKUResponse = {
        id: tempId,
        name: payload.name,
        sku_code: payload.sku_code ?? null,
        category: payload.category ?? null,
        description: payload.description ?? null,
        variants: payload.variants,
        created_by: "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_used_at: null,
      };

      queryClient.setQueryData<ProductSKUResponse[]>(key, (old) => [
        optimisticSku,
        ...(old ?? []),
      ]);

      return { previousSkus, tempId };
    },
    onSuccess: (newSku, _payload, context) => {
      queryClient.setQueryData<ProductSKUResponse[]>(
        productSKUQueryKey(brandId ?? ""),
        (old) =>
          old?.map((s) => (s.id === context?.tempId ? newSku : s)) ?? [newSku]
      );
      queryClient.invalidateQueries({
        queryKey: ["product-skus-filter", brandId],
      });
      queryClient.invalidateQueries({
        queryKey: productSKUCategoriesQueryKey(brandId ?? ""),
      });
    },
    onError: (_err, _payload, context) => {
      if (context?.previousSkus !== undefined) {
        queryClient.setQueryData(
          productSKUQueryKey(brandId ?? ""),
          context.previousSkus
        );
      }
    },
  });
}

export function useUpdateProductSKU(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      skuId,
      payload,
    }: {
      skuId: string;
      payload: UpdateProductSKUPayload;
    }) => updateProductSKU(brandId!, skuId, payload),
    onMutate: async ({ skuId, payload }) => {
      const key = productSKUQueryKey(brandId ?? "");
      await queryClient.cancelQueries({ queryKey: key });
      const previousSkus = queryClient.getQueryData<ProductSKUResponse[]>(key);

      queryClient.setQueryData<ProductSKUResponse[]>(
        key,
        (old) =>
          old?.map((s) =>
            s.id === skuId
              ? { ...s, ...payload, updated_at: new Date().toISOString() }
              : s
          ) ?? []
      );

      return { previousSkus };
    },
    onSuccess: (updatedSku) => {
      queryClient.setQueryData<ProductSKUResponse[]>(
        productSKUQueryKey(brandId ?? ""),
        (old) =>
          old?.map((s) => (s.id === updatedSku.id ? updatedSku : s)) ?? []
      );
    },
    onError: (_err, _payload, context) => {
      if (context?.previousSkus !== undefined) {
        queryClient.setQueryData(
          productSKUQueryKey(brandId ?? ""),
          context.previousSkus
        );
      }
    },
  });
}

export function useBulkCreateProductSKU(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkCreateProductSKUPayload) =>
      bulkCreateProductSKUs(brandId!, payload),
    onMutate: async (payload) => {
      const key = productSKUQueryKey(brandId ?? "");
      await queryClient.cancelQueries({ queryKey: key });
      const previousSkus = queryClient.getQueryData<ProductSKUResponse[]>(key);

      const now = new Date().toISOString();
      const optimisticSkus: ProductSKUResponse[] = payload.items.map(
        (item, i) => ({
          id: `_optimistic_bulk_${Date.now()}_${i}`,
          name: item.name,
          sku_code: item.sku_code ?? null,
          category: item.category ?? null,
          description: item.description ?? null,
          variants: item.variants.map((v, vi) => ({
            id: `_optimistic_variant_${Date.now()}_${i}_${vi}`,
            name: v.name,
            products: v.products.map((p) => ({
              gallery_item_id: "",
              product_url: p.url,
              angle: p.angle ?? "front",
            })),
          })),
          created_by: "",
          created_at: now,
          updated_at: now,
          last_used_at: null,
        })
      );

      queryClient.setQueryData<ProductSKUResponse[]>(key, (old) => [
        ...optimisticSkus,
        ...(old ?? []),
      ]);

      return { previousSkus, tempIds: optimisticSkus.map((s) => s.id) };
    },
    onSuccess: (newSkus, _payload, context) => {
      const key = productSKUQueryKey(brandId ?? "");
      const tempIds = new Set(context?.tempIds ?? []);
      queryClient.setQueryData<ProductSKUResponse[]>(key, (old) => [
        ...newSkus,
        ...(old ?? []).filter((s) => !tempIds.has(s.id)),
      ]);
      queryClient.invalidateQueries({
        queryKey: ["product-skus-filter", brandId],
      });
      queryClient.invalidateQueries({
        queryKey: productSKUCategoriesQueryKey(brandId ?? ""),
      });
    },
    onError: (_err, _payload, context) => {
      if (context?.previousSkus !== undefined) {
        queryClient.setQueryData(
          productSKUQueryKey(brandId ?? ""),
          context.previousSkus
        );
      }
    },
  });
}

export function useGroupAnalyzeProductSKU(brandId: string | null) {
  return useMutation({
    mutationFn: (payload: GroupAnalyzePayload) =>
      groupAnalyzeProductSKU(brandId!, payload),
  });
}

const PRODUCT_SKU_FILTER_PAGE_SIZE = 20;

export function useFilterProductSKUs(
  brandId: string | null,
  categories: string[] | null
) {
  return useInfiniteQuery<ProductSKUFilterResponse[]>({
    queryKey: ["product-skus-filter", brandId, categories],
    queryFn: ({ pageParam = 0 }) =>
      filterProductSKUs(brandId!, {
        categories: categories?.length ? categories : null,
        skip: pageParam as number,
        limit: PRODUCT_SKU_FILTER_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PRODUCT_SKU_FILTER_PAGE_SIZE) return undefined;
      return allPages.length * PRODUCT_SKU_FILTER_PAGE_SIZE;
    },
    initialPageParam: 0,
    enabled: !!brandId,
    staleTime: 1000 * 60 * 2,
  });
}
